import { useContext, useMemo, useState } from 'react';
import { useParams } from 'next/navigation';
import { getUrl, IDENTIFIERS } from '@helpers/urlsHelper';
import ListAndDetailsSubLayout from '@layouts/ListAndDetailsSubLayout';
import useIsMobile, { SIZES } from '@hooks/useIsMobile';
import Plus from '@components/Icons/Plus';
import DashboardContext from '@layouts/DashboardLayout/DashboardContext';
import removeNullish from '@helpers/removeNullish';
import { NoodleProductTypes } from '@typings/graphql-models';
import { isChargeProduct, isHandbookProduct, isSubscriptionProduct } from '@helpers/isProductType';
import hasNoodleProductType from '@helpers/hasNoodleProductType';
import * as format from '@format';
import ArrowsDownUp from '@components/Icons/ArrowsDownUp';
import ReorderServiceModal from '@modals/ReorderServiceModal';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import { useToast } from '@hooks';
import sortProductsByActiveThenOrder from '@helpers/sortProducts';

const Services: React.FC = ({ children }) => {
  const pathParams = useParams<{ productSlug?: string }>();
  const isOneColumn = useIsMobile(SIZES.md);
  const { setIsCreateServiceModalOpen, products, fetchProducts } = useContext(DashboardContext);
  const [searchTerms, setSearchTerms] = useState<string | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [reorderModalOpen, setReorderModalOpen] = useState(false);
  const [reOrderedItems, setReorderedItems] = useState<{ id: string }[]>([]);

  const { getData: updateProductOrder, fetchingState: updatingProductState } = useNoodleApi(tsClient.products.updateProductOrder, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Products updated successfully!'],
  });

  const selectedSlug = pathParams?.productSlug;

  const productMap = useMemo(() => {
    if (!products) return new Map();
    return new Map(products.map(product => [product.id, product]));
  }, [products]);

  const sortedProducts = useMemo(() => {
    if (!products) return [];

    if (reOrderedItems.length === 0) {
      return sortProductsByActiveThenOrder(products);
    }

    return reOrderedItems.flatMap(({ id }) => {
      const product = productMap.get(id);
      return product ? [product] : [];
    });
  }, [products, productMap, reOrderedItems]);

  const isNotService = (product: {
    productTypes: {
      noodleProductType?: NoodleProductTypes | null;
    }[];
  }): boolean =>
    isChargeProduct(product)
    || isHandbookProduct(product)
    || hasNoodleProductType(product, NoodleProductTypes.Response, NoodleProductTypes.ProductGroup)
    || isSubscriptionProduct(product);

  const items = useMemo(() => {
    if (!products) {
      return [];
    }

    const filteredProducts = products
      .filter(p => !isNotService(p));
    return sortProductsByActiveThenOrder(filteredProducts)
      .map(product => ({
        bottomLine:
          product.description
          || product.prices
            .map(price => price.priceTitle)
            .filter(removeNullish)
            .filter(title => title !== '')
            .join(', '),
        href: getUrl(IDENTIFIERS.DASHBOARD_SERVICES_DETAILS, { productSlug: product.slug }),
        id: product.id,
        isMuted: !product.isActive,
        isSelected: product.slug === selectedSlug,
        label: product.title || 'No title',
        slug: product.slug,
        topLine: (product.productTypes?.[0]?.noodleProductType && format.productType(product.productTypes[0]?.noodleProductType)) || '',
      }));
  }, [products, selectedSlug]);

  const filters = useMemo(() => {
    if (!products) {
      return [];
    }
    const productTypes = products
      .filter(p => !isNotService(p))
      .map(product => ({
        isChecked: product.productTypes?.[0]?.noodleProductType ? selectedFilters.includes(product.productTypes[0]?.noodleProductType) : false,
        key: product.productTypes?.[0]?.noodleProductType || '',
        label: (product.productTypes?.[0]?.noodleProductType && format.productType(product.productTypes?.[0]?.noodleProductType)) || '',
      }));
    return productTypes.filter((productType, index) => productTypes.findIndex(p => p.key === productType.key) === index);
  }, [products, selectedFilters]);

  const handleReorderSave = async (): Promise<void> => {
    const reorderedProducts = reOrderedItems.map((product, index) => ({
      order: index + 1,
      productId: product.id,
    }));

    await updateProductOrder(reorderedProducts);
    setReorderModalOpen(false);
  };

  return (
    <>
      {reorderModalOpen && (
        <ReorderServiceModal
          items={sortedProducts ? sortedProducts.filter(p => p.isActive) : []}
          onClose={() => {
            setReorderModalOpen(false);
            setReorderedItems([]);
          }}
          onOrderChange={async (newOrder: { id: string }[]) => {
            setReorderedItems(newOrder);
          }}
          fetchingState={updatingProductState.isFetching}
          onSave={async () => {
            await handleReorderSave();
            fetchProducts();
            setReorderModalOpen(false);
          }}
        />
      )}
      <ListAndDetailsSubLayout
        header={{
          actions: [
            {
              icon: Plus,
              label: 'Create new service',
              onClick: () => {
                setIsCreateServiceModalOpen(true);
              },
            },
            {
              icon: ArrowsDownUp,
              isSecondary: true,
              label: 'Reorder',
              onClick: () => {
                setReorderModalOpen(true);
              },
            },
          ],
          title: 'Your services',
        }}
        filters={{
          items: [
            {
              isMultiSelect: true,
              items: filters,
              label: 'Type',
              onChange: setSelectedFilters,
            },
          ],
        }}
        onSearch={setSearchTerms}
        items={items
          .filter(i => (searchTerms ? i.label.toLowerCase().includes(searchTerms?.toLowerCase()) : true))
          .filter(i =>
            selectedFilters.length > 0 && i.topLine
              ? selectedFilters
                .map(f => format.productType(f as NoodleProductTypes)).includes(i.topLine)
              : true,
          )}
        selectedId={selectedSlug}
        ifNoItemsText={'No products yet'}
        isFetchingList={products === null}
        isOneColumn={isOneColumn}
        getDetailPagePath={() => getUrl(IDENTIFIERS.DASHBOARD_SERVICES_DETAILS, { productSlug: items[0].slug })}
        reloadListView={async () => {
          await fetchProducts();
        }}
        resourceType={'services'}
      >
        {children}
      </ListAndDetailsSubLayout>
    </>
  );
};

export default Services;
